import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchBlogData, prefetchBlogPost } from '../lib/publicBlogApi';
import BlogSkeleton from '../components/BlogSkeleton';
import { ArrowRight, AlertCircle, Search, X } from 'lucide-react';
import GlobalHeader from '../components/GlobalHeader';
import GlobalFooter from '../components/GlobalFooter';
import PageSEO from '../components/PageSEO';
import { getBlogImageUrl } from '../lib/utils';
import './BlogPostPage.css';
import { blogUrl } from '../lib/blogSeo';

export default function BlogListingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState(false);
  const nextPage = useRef(3); // The first 12 posts cover two pages of six.
  const generation = useRef(0);
  const moreRequest = useRef(false);

  const selectedCategory = searchParams.get('category') || '';
  const selectedTag = searchParams.get('tag') || '';
  const search = searchParams.get('search') || '';
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);

  const submitSearch = event => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    const term = searchInput.trim();
    if (term) next.set('search', term);
    else next.delete('search');
    setSearchParams(next);
  };

  const clearSearch = () => {
    setSearchInput('');
    const next = new URLSearchParams(searchParams);
    next.delete('search');
    setSearchParams(next);
  };

  useEffect(() => {
    let active = true;
    fetchBlogData('/public/blog-categories')
      .then(data => { if (active) setCategories(data || []); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const currentGeneration = ++generation.current;
    setLoading(true);
    setError(false);
    setLoadingMore(false);
    setMoreError(false);
    moreRequest.current = false;
    nextPage.current = 3;
    fetchBlogData('/public/blogs', {
      page: 1, limit: 12,
      category_slug: selectedCategory || undefined,
      tag: selectedTag || undefined,
      search: search || undefined
    }).then(data => {
      if (active) {
        const items = data.items || [];
        setPosts(items);
        setTotal(data.total || 0);
        if (items.length > 0 && typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(() => {
            items.slice(0, 4).forEach(p => {
              if (p?.slug) prefetchBlogPost(p.slug);
            });
          });
        }
      }
    }).catch(() => {
      if (active) setError(true);
    }).finally(() => { if (active) setLoading(false); });
    return () => {
      active = false;
      if (generation.current === currentGeneration) generation.current++;
    };
  }, [selectedCategory, selectedTag, search, attempt]);

  const loadMore = async () => {
    if (moreRequest.current || loading || posts.length >= total) return;
    moreRequest.current = true;
    const currentGeneration = generation.current;
    setLoadingMore(true);
    setMoreError(false);
    try {
      const data = await fetchBlogData('/public/blogs', {
        page: nextPage.current, limit: 6,
        category_slug: selectedCategory || undefined,
        tag: selectedTag || undefined,
        search: search || undefined
      });
      if (generation.current !== currentGeneration) return;
      setPosts(previous => {
        const known = new Set(previous.map(post => post.id));
        return [...previous, ...(data.items || []).filter(post => !known.has(post.id))];
      });
      setTotal(data.total || 0);
      nextPage.current++;
    } catch {
      if (generation.current === currentGeneration) setMoreError(true);
    } finally {
      if (generation.current === currentGeneration) {
        moreRequest.current = false;
        setLoadingMore(false);
      }
    }
  };

  const handleCategoryClick = (slug) => {
    const next = new URLSearchParams(searchParams);
    if (slug) {
      next.set('category', slug);
    } else {
      next.delete('category');
      next.delete('tag');
    }
    setSearchParams(next);
  };

  return (
    <div className="blog-post-page blog-index-page">
      <PageSEO
        title="The AI Search Return Blog — Insights & Analysis | AI Search Advisers"
        description="Explore research, playbooks, and strategic perspectives on generative search, brand discovery, and AI visibility."
        canonical="https://aisearchadvisers.com/blogs"
      />

      <GlobalHeader />

      {/* Hero Banner */}
      <header className="blog-hero blog-index-hero">
        <div className="blog-hero-inner">
          <div className="blog-category-eyebrow">
            <span className="blog-category-name">EDITORIAL & RESEARCH</span>
          </div>

          <h1 className="blog-hero-title">
            The AI Search Return Blog
          </h1>

          <p className="blog-hero-excerpt">
            Ideas, research, and practical guides to help your business get discovered in AI search.
          </p>

          <form className="blog-search-form" role="search" aria-label="Search blog articles" onSubmit={submitSearch}>
            <Search size={20} aria-hidden="true" />
            <input type="search" aria-label="Search articles" placeholder="I'm looking for..." value={searchInput} onChange={event => setSearchInput(event.target.value)} />
            {searchInput && <button type="button" className="blog-search-clear" onClick={clearSearch} aria-label="Clear search"><X size={18} /></button>}
            <button type="submit" className="blog-search-submit">Search <ArrowRight size={16} aria-hidden="true" /></button>
          </form>
        </div>
      </header>

      <main className="blog-listing-container" id="blog-results">
        <div className="blog-listing-toolbar">
          <div>
            <p className="blog-section-eyebrow">THE LATEST THINKING</p>
            <h2>{search ? 'Search results' : selectedCategory || selectedTag ? 'Explore articles' : 'Latest articles'}</h2>
          </div>
          <div className="blog-category-filters" aria-label="Filter by category">
            <button type="button" onClick={() => handleCategoryClick('')} aria-pressed={!selectedCategory && !selectedTag}>All articles</button>
            {categories.map(cat => <button key={cat.id} type="button" onClick={() => handleCategoryClick(cat.slug)} aria-pressed={selectedCategory === cat.slug}>{cat.name}</button>)}
          </div>
        </div>
        {!loading && !error && <div className="blog-results-summary" role="status">
          <span>{search ? `${total} ${total === 1 ? 'result' : 'results'} for “${search}”` : `${total} ${total === 1 ? 'article' : 'articles'}`}{selectedTag ? ` · Tagged: ${selectedTag}` : ''}</span>
          {(search || selectedTag || selectedCategory) && <button type="button" onClick={() => { setSearchInput(''); setSearchParams({}); }}>Clear filters <X size={14} aria-hidden="true" /></button>}
        </div>}
        {loading ? (
          <BlogSkeleton count={12} />
        ) : error ? (
          <div className="blog-load-error" role="alert">
            <AlertCircle size={28} />
            <h2>Unable to load articles</h2>
            <p>Please try again in a moment.</p>
            <button type="button" onClick={() => setAttempt(value => value + 1)}>Try again</button>
          </div>
        ) : posts.length === 0 ? (
          <div style={{ background: '#FFFFFF', border: '1px solid #DFD9CE', padding: '64px 24px', textAlign: 'center', borderRadius: '4px' }}>
            <h3 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.75rem', color: '#06203A', marginBottom: '8px' }}>
              No Articles Found
            </h3>
            <p style={{ color: '#718096', fontSize: '0.9rem', marginBottom: '20px' }}>
              We haven&apos;t published any articles matching this filter yet. Check back soon!
            </p>
            <button
              onClick={() => setSearchParams({})}
              style={{ background: '#FEAB3D', color: '#021426', border: 'none', padding: '8px 20px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="blog-related-grid">
            {posts.map((post, index) => (
              <Link
                key={post.id}
                to={new URL(blogUrl(post)).pathname}
                state={{ initialPost: post }}
                onMouseEnter={() => prefetchBlogPost(post.slug)}
                onFocus={() => prefetchBlogPost(post.slug)}
                className="blog-related-card"
              >
                {post.featured_image ? (
                  <img
                    loading={index < 3 ? 'eager' : 'lazy'}
                    decoding="async"
                    src={getBlogImageUrl(post.featured_image)}
                    alt={post.title}
                    className="blog-related-card-image"
                  />
                ) : (
                  <div className="blog-related-card-image blog-card-placeholder">
                    AI Search Advisers
                  </div>
                )}

                <div className="blog-related-card-body">
                  <div className="blog-card-meta">
                    <span className="blog-related-card-category">{post.category_name || 'General'}</span>
                    {post.published_at && !Number.isNaN(Date.parse(post.published_at)) && <time dateTime={post.published_at}>{new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</time>}
                  </div>
                  <h3 className="blog-related-card-title">
                    {post.title}
                  </h3>
                  <p className="blog-related-card-excerpt">
                    {post.excerpt || "Read our strategic perspective on generative search, brand authority, and digital visibility."}
                  </p>

                  <div className="blog-related-card-footer">
                    <span>{post.author_name || "Editorial"}</span>
                    <span className="blog-related-card-read">
                      <span>Read article</span>
                      <ArrowRight size={12} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
        {!loading && !error && posts.length > 0 && (
          <div className="blog-load-more">
            {loadingMore && <BlogSkeleton count={6} />}
            {moreError && <p role="alert">Unable to load more articles. Please try again.</p>}
            {posts.length < total && (
              <button type="button" onClick={loadMore} disabled={loadingMore} className="blog-load-more-button">
                {loadingMore ? 'Loading...' : moreError ? 'Try again' : 'Load more'}
              </button>
            )}
          </div>
        )}
      </main>

      <GlobalFooter />
    </div>
  );
}
